import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "sonner";
import {
  Loader2,
  RefreshCcw,
  Eye,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  EmailLog,
  EmailLogPage,
  EmailLogStatus,
  GET_EMAIL_LOGS,
  RETRY_EMAIL_LOG,
} from "../../../graphql/email";
import { cn } from "../../../lib/utils";

const STATUS_LABELS: Record<EmailLogStatus, string> = {
  PENDING: "Oczekuje",
  SENT: "Wysłano",
  FAILED: "Błąd",
  SKIPPED_DISABLED: "Pominięte",
};

const STATUS_BADGE_CLASSES: Record<EmailLogStatus, string> = {
  PENDING:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300 border-yellow-300/40",
  SENT: "bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300 border-green-300/40",
  FAILED:
    "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300 border-red-300/40",
  SKIPPED_DISABLED:
    "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300 border-gray-300/40",
};

const TEMPLATE_LABELS: Record<string, string> = {
  PROJECT_MEMBER_ADDED: "Dodanie do projektu",
  TASK_ASSIGNED: "Przypisanie zadania",
  PROJECT_STATUS_CHANGED: "Zmiana statusu projektu",
  TASK_STATUS_CHANGED: "Zmiana statusu zadania",
  TASK_COMMENT_ADDED: "Nowy komentarz w zadaniu",
};

const PAGE_SIZE = 50;

function formatDate(s: string | null | undefined): string {
  if (!s) return "—";
  return new Date(s).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function useDebounced<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export const EmailLogsTab: React.FC = () => {
  const [status, setStatus] = useState<string>("all");
  const [toSearchRaw, setToSearchRaw] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [until, setUntil] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [selected, setSelected] = useState<EmailLog | null>(null);

  const toSearch = useDebounced(toSearchRaw, 400);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [status, toSearch, from, until]);

  const filter = useMemo(() => {
    const f: any = { page };
    if (status !== "all") f.status = status;
    if (toSearch.trim() !== "") f.toSearch = toSearch.trim();
    if (from !== "") f.from = new Date(from + "T00:00:00").toISOString();
    if (until !== "") f.until = new Date(until + "T23:59:59").toISOString();
    return f;
  }, [status, toSearch, from, until, page]);

  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<{
    emailLogs: EmailLogPage;
  }>(GET_EMAIL_LOGS, {
    variables: { filter },
    fetchPolicy: "network-only",
  });

  const [retry, { loading: retrying }] = useMutation(RETRY_EMAIL_LOG, {
    onCompleted: () => {
      toast.success("Ponowiono wysyłkę");
      refetch();
    },
    onError: (err) => {
      toast.error(`Błąd: ${err.message}`);
    },
  });

  const logs = data?.emailLogs.items || [];
  const total = data?.emailLogs.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Emails dispatch asynchronously: a row is created as PENDING and flips to
  // SENT/FAILED a moment later. Auto-refresh while any PENDING row is visible so
  // the status doesn't appear "stuck" on Oczekuje; stop once everything settled.
  const hasPending = logs.some((l) => l.status === "PENDING");
  useEffect(() => {
    if (hasPending) {
      startPolling(4000);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [hasPending, startPolling, stopPolling]);

  const clearFilters = () => {
    setStatus("all");
    setToSearchRaw("");
    setFrom("");
    setUntil("");
    setPage(1);
  };

  const hasFilters =
    status !== "all" || toSearchRaw !== "" || from !== "" || until !== "";

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Filtry</CardTitle>
          <CardDescription>
            Zawęź widok historii wysyłek
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <div className="space-y-1">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie</SelectItem>
                  <SelectItem value="PENDING">Oczekuje</SelectItem>
                  <SelectItem value="SENT">Wysłano</SelectItem>
                  <SelectItem value="FAILED">Błąd</SelectItem>
                  <SelectItem value="SKIPPED_DISABLED">Pominięte</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="toSearch">Odbiorca (email)</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="toSearch"
                  className="pl-8"
                  value={toSearchRaw}
                  onChange={(e) => setToSearchRaw(e.target.value)}
                  placeholder="np. jan@firma.pl"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="from">Od</Label>
              <Input
                id="from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="until">Do</Label>
              <Input
                id="until"
                type="date"
                value={until}
                onChange={(e) => setUntil(e.target.value)}
              />
            </div>
          </div>
          {hasFilters && (
            <div className="mt-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Wyczyść filtry
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Historia wysyłek</CardTitle>
              <CardDescription>
                {loading
                  ? "Ładowanie..."
                  : `Znaleziono ${total} ${total === 1 ? "wpis" : "wpisów"}`}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <RefreshCcw
                className={cn("h-4 w-4", loading && "animate-spin")}
              />
              Odśwież
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <p className="text-red-600 text-sm py-4">
              Błąd: {error.message}
            </p>
          )}
          {!error && loading && logs.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {!error && !loading && logs.length === 0 && (
            <p className="text-center text-muted-foreground py-12">
              Brak wpisów dla wybranych filtrów.
            </p>
          )}
          {logs.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Do</TableHead>
                    <TableHead>Temat</TableHead>
                    <TableHead>Szablon</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Próby</TableHead>
                    <TableHead className="text-right">Akcje</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow
                      key={log.id}
                      className="cursor-pointer"
                      onClick={() => setSelected(log)}
                    >
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDate(log.sentAt || log.createdAt)}
                      </TableCell>
                      <TableCell className="text-sm font-mono">
                        {log.to}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm">
                        {log.subject}
                      </TableCell>
                      <TableCell className="text-sm">
                        {log.templateKey
                          ? TEMPLATE_LABELS[log.templateKey] || log.templateKey
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn(
                            "border",
                            STATUS_BADGE_CLASSES[log.status]
                          )}
                        >
                          {STATUS_LABELS[log.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {log.attemptCount}
                      </TableCell>
                      <TableCell
                        className="text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelected(log)}
                            title="Podgląd"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {log.status === "FAILED" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                retry({ variables: { id: log.id } })
                              }
                              disabled={retrying}
                              title="Ponów wysyłkę"
                            >
                              {retrying ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <RefreshCcw className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Strona {page} z {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                  className="flex items-center gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Poprzednia
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={page === totalPages || loading}
                  className="flex items-center gap-1"
                >
                  Następna
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview dialog */}
      <Dialog
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Szczegóły wysyłki</DialogTitle>
            <DialogDescription>
              {selected && formatDate(selected.sentAt || selected.createdAt)}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="overflow-y-auto space-y-4 pr-1">
              <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted-foreground">Do</p>
                  <p className="font-mono">{selected.to}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <Badge
                    variant="outline"
                    className={cn(
                      "border",
                      STATUS_BADGE_CLASSES[selected.status]
                    )}
                  >
                    {STATUS_LABELS[selected.status]}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Szablon</p>
                  <p>
                    {selected.templateKey
                      ? TEMPLATE_LABELS[selected.templateKey] ||
                        selected.templateKey
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Liczba prób</p>
                  <p>{selected.attemptCount}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Wysłano</p>
                  <p>{formatDate(selected.sentAt)}</p>
                </div>
                {selected.nextRetryAt && (
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Kolejna próba
                    </p>
                    <p>{formatDate(selected.nextRetryAt)}</p>
                  </div>
                )}
                {selected.contextType && (
                  <div>
                    <p className="text-xs text-muted-foreground">Kontekst</p>
                    <p className="font-mono text-xs">
                      {selected.contextType}
                      {selected.contextId
                        ? ` · ${selected.contextId.slice(0, 8)}…`
                        : ""}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">Temat</p>
                <p className="font-medium">{selected.subject}</p>
              </div>

              {selected.errorMessage && (
                <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3">
                  <p className="text-xs text-muted-foreground mb-1">
                    Komunikat błędu
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap font-mono">
                    {selected.errorMessage}
                  </p>
                </div>
              )}

              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  Podgląd treści
                </p>
                <div className="rounded-md border border-border overflow-hidden">
                  <iframe
                    title="Podgląd wiadomości"
                    sandbox=""
                    srcDoc={selected.bodyHtml}
                    className="w-full bg-white"
                    style={{ minHeight: 400, border: 0 }}
                  />
                </div>
              </div>

              {selected.status === "FAILED" && (
                <div className="flex justify-end">
                  <Button
                    onClick={() =>
                      retry({ variables: { id: selected.id } }).then(() =>
                        setSelected(null)
                      )
                    }
                    disabled={retrying}
                    className="flex items-center gap-2"
                  >
                    {retrying ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCcw className="h-4 w-4" />
                    )}
                    Ponów wysyłkę
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
